import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Extension, type JSONContent, type MarkdownParseHelpers, type MarkdownRendererHelpers, type MarkdownToken, type RenderContext } from "@tiptap/core";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Paragraph from "@tiptap/extension-paragraph";
import { TableKit } from "@tiptap/extension-table";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { Markdown } from "@tiptap/markdown";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import { BubbleMenu, type BubbleMenuProps } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { Icon, Tooltip, type IconName } from "@bedrock/ui";
import { LinkMenu, linkEditRequestFromSelection, type LinkEditRequest } from "./link-menu";
import "./styles.css";

export interface MarkdownEditorProps {
  markdown: string;
  onChange: (markdown: string) => void;
  onCommit?: (markdown: string) => void;
  hiddenFirstHeading?: string | readonly string[] | undefined;
  autofocus?: boolean;
  compact?: boolean;
  focusPoint?: { x: number; y: number };
  showToolbar?: boolean;
  floatingToolbar?: boolean;
  bottomScrollClearance?: number;
  enableTables?: boolean;
  shortcuts?: MarkdownEditorShortcuts;
  initialCursor?: number | undefined;
  onCursorChange?: ((cursor: number) => void) | undefined;
}

export interface MarkdownEditorHandle {
  focusFirstParagraph: () => boolean;
  openSearch: () => void;
}

export const EDITOR_TOOLBAR_SCROLL_CLEARANCE = 120;

export type MarkdownEditorShortcutAction =
  | "text-bold"
  | "text-italic"
  | "text-underline"
  | "text-paragraph"
  | "text-heading-one"
  | "text-heading-two"
  | "text-heading-three"
  | "text-heading-four"
  | "text-heading-five"
  | "text-align-left"
  | "text-align-center"
  | "text-align-right"
  | "text-align-justify"
  | "text-bullet-list"
  | "text-ordered-list"
  | "text-checklist"
  | "text-quote"
  | "text-code-block"
  | "text-highlight";

export interface MarkdownEditorShortcuts {
  actions: Record<MarkdownEditorShortcutAction, { display: string; aria: string }>;
  actionForEvent: (event: KeyboardEvent) => MarkdownEditorShortcutAction | null;
  defaultActionForEvent: (event: KeyboardEvent) => MarkdownEditorShortcutAction | null;
}

export interface MarkdownPreviewProps {
  markdown: string;
  className?: string;
  empty?: string;
  hiddenHeading?: string | readonly string[];
  onTaskToggle?: (markdown: string) => void;
}

type TextAlignment = "left" | "center" | "right" | "justify";

interface DocumentSearchMatch {
  from: number;
  to: number;
}

interface DocumentSearchPluginState {
  activeIndex: number;
  caseSensitive: boolean;
  decorations: DecorationSet;
  matches: DocumentSearchMatch[];
  query: string;
}

interface DocumentSearchMeta {
  activeIndex: number;
  caseSensitive: boolean;
  query: string;
}

const documentSearchPluginKey = new PluginKey<DocumentSearchPluginState>("bedrock-document-search");

function documentSearchMatches(doc: ProseMirrorNode, query: string, caseSensitive: boolean): DocumentSearchMatch[] {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return [];
  const escapedQuery = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(escapedQuery, caseSensitive ? "gu" : "giu");
  const matches: DocumentSearchMatch[] = [];

  doc.descendants((node, position) => {
    if (!node.isText || !node.text) return;
    pattern.lastIndex = 0;
    for (const match of node.text.matchAll(pattern)) {
      if (match.index === undefined) continue;
      matches.push({ from: position + match.index, to: position + match.index + match[0].length });
    }
  });
  return matches;
}

function buildDocumentSearchState(doc: ProseMirrorNode, query: string, requestedIndex: number, caseSensitive: boolean): DocumentSearchPluginState {
  const matches = documentSearchMatches(doc, query, caseSensitive);
  const activeIndex = matches.length === 0 ? -1 : ((requestedIndex % matches.length) + matches.length) % matches.length;
  const decorations = DecorationSet.create(doc, matches.map((match, index) => Decoration.inline(match.from, match.to, {
    class: index === activeIndex ? "bedrock-search-match bedrock-search-match--active" : "bedrock-search-match"
  })));
  return { activeIndex, caseSensitive, decorations, matches, query };
}

const DocumentSearch = Extension.create({
  name: "documentSearch",
  addProseMirrorPlugins() {
    return [new Plugin<DocumentSearchPluginState>({
      key: documentSearchPluginKey,
      state: {
        init: (_, state) => buildDocumentSearchState(state.doc, "", -1, false),
        apply: (transaction, previous) => {
          const meta = transaction.getMeta(documentSearchPluginKey) as DocumentSearchMeta | undefined;
          if (!transaction.docChanged && !meta) return previous;
          return buildDocumentSearchState(
            transaction.doc,
            meta?.query ?? previous.query,
            meta?.activeIndex ?? previous.activeIndex,
            meta?.caseSensitive ?? previous.caseSensitive
          );
        }
      },
      props: {
        decorations: (state) => documentSearchPluginKey.getState(state)?.decorations ?? null
      }
    })];
  }
});

function setDocumentSearch(editor: Editor, query: string, requestedIndex: number, caseSensitive: boolean): DocumentSearchPluginState {
  editor.view.dispatch(editor.state.tr.setMeta(documentSearchPluginKey, { query, activeIndex: requestedIndex, caseSensitive } satisfies DocumentSearchMeta));
  const search = documentSearchPluginKey.getState(editor.state) ?? buildDocumentSearchState(editor.state.doc, query, requestedIndex, caseSensitive);
  const match = search.matches[search.activeIndex];
  if (match) editor.chain().setTextSelection(match).scrollIntoView().run();
  return search;
}

function isDocumentSearchShortcut(event: KeyboardEvent): boolean {
  return !event.altKey && !event.shiftKey && event.code === "KeyF" && event.metaKey !== event.ctrlKey && (event.metaKey || event.ctrlKey);
}

interface PreviewText {
  text: string;
  textAlign?: TextAlignment;
}

interface PreviewTask extends PreviewText {
  checked: boolean;
  lineIndex: number;
}

type TableAlignment = "left" | "center" | "right" | null;

interface PreviewTable {
  headers: PreviewText[];
  alignments: TableAlignment[];
  rows: PreviewText[][];
}

const taskLinePattern = /^\s*[-*+]\s+\[([ xX])\](?:\s+(.*))?$/;
const unorderedLinePattern = /^\s*[-*+](?:\s+(.*))?$/;
const orderedLinePattern = /^\s*\d+[.)](?:\s+(.*))?$/;

type PreviewBlock =
  | { kind: "heading"; level: 1 | 2 | 3 | 4 | 5; content: PreviewText }
  | { kind: "paragraph"; content: PreviewText }
  | { kind: "tasks"; items: PreviewTask[] }
  | { kind: "unordered"; items: PreviewText[] }
  | { kind: "ordered"; items: PreviewText[] }
  | { kind: "table"; table: PreviewTable }
  | { kind: "quote"; content: PreviewText }
  | { kind: "code"; text: string };

const textAlignmentMarkerSource = String.raw`(?:<!--|&lt;!--)\s*bedrock-text-align:(left|center|right|justify)\s*(?:-->|--&gt;)`;
const previewTextPaddingSource = String.raw`(?:\s|\u200b|\u200c|\u200d|\u2060|\ufeff)`;
const textAlignmentMarkerPattern = new RegExp(`${previewTextPaddingSource}*${textAlignmentMarkerSource}${previewTextPaddingSource}*$`, "i");
const textAlignmentMarkerGlobalPattern = new RegExp(textAlignmentMarkerSource, "gi");
const previewTextTrailingPaddingPattern = new RegExp(`${previewTextPaddingSource}+$`, "u");

function previewText(source: string): PreviewText {
  const marker = source.match(textAlignmentMarkerPattern);
  const text = source.replace(textAlignmentMarkerGlobalPattern, "").replace(previewTextTrailingPaddingPattern, "");
  if (!marker) return { text };
  return {
    text,
    textAlign: marker[1]!.toLowerCase() as TextAlignment
  };
}

function splitTableRow(line: string): string[] {
  const source = line.trim();
  const cells: string[] = [];
  let cell = "";
  let codeFenceLength = 0;

  for (let index = 0; index < source.length;) {
    const character = source[index]!;
    if (character === "\\" && source[index + 1] === "|") {
      cell += "|";
      index += 2;
      continue;
    }
    if (character === "`") {
      let runLength = 1;
      while (source[index + runLength] === "`") runLength += 1;
      if (codeFenceLength === 0) codeFenceLength = runLength;
      else if (codeFenceLength === runLength) codeFenceLength = 0;
      cell += source.slice(index, index + runLength);
      index += runLength;
      continue;
    }
    if (character === "|" && codeFenceLength === 0) {
      cells.push(cell.trim());
      cell = "";
      index += 1;
      continue;
    }
    cell += character;
    index += 1;
  }
  cells.push(cell.trim());

  if (source.startsWith("|")) cells.shift();
  if (source.endsWith("|") && !source.endsWith("\\|")) cells.pop();
  return cells;
}

function tableAlignment(cell: string): TableAlignment | undefined {
  const separator = cell.trim();
  if (!/^:?-{3,}:?$/.test(separator)) return undefined;
  if (separator.startsWith(":") && separator.endsWith(":")) return "center";
  if (separator.endsWith(":")) return "right";
  if (separator.startsWith(":")) return "left";
  return null;
}

function parseTableAt(lines: string[], index: number): { table: PreviewTable; nextIndex: number } | null {
  const headerLine = lines[index];
  const separatorLine = lines[index + 1];
  if (!headerLine?.includes("|") || !separatorLine?.includes("|")) return null;

  const headers = splitTableRow(headerLine);
  const separatorCells = splitTableRow(separatorLine);
  if (!headers.length || headers.length !== separatorCells.length) return null;
  const alignments = separatorCells.map(tableAlignment);
  if (alignments.some((alignment) => alignment === undefined)) return null;

  const rows: PreviewText[][] = [];
  let nextIndex = index + 2;
  while (nextIndex < lines.length) {
    const rowLine = lines[nextIndex] ?? "";
    if (!rowLine.trim() || !rowLine.includes("|")) break;
    const cells = splitTableRow(rowLine).slice(0, headers.length);
    while (cells.length < headers.length) cells.push("");
    rows.push(cells.map(previewText));
    nextIndex += 1;
  }

  return { table: { headers: headers.map(previewText), alignments: alignments as TableAlignment[], rows }, nextIndex };
}

function isBlockStart(lines: string[], index: number) {
  const line = lines[index] ?? "";
  return /^(#{1,5})\s+/.test(line)
    || taskLinePattern.test(line)
    || unorderedLinePattern.test(line)
    || orderedLinePattern.test(line)
    || /^\s*>\s?/.test(line)
    || /^\s*```/.test(line)
    || parseTableAt(lines, index) !== null;
}

function parseMarkdownBlocks(markdown: string): PreviewBlock[] {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: PreviewBlock[] = [];

  for (let index = 0; index < lines.length;) {
    const line = lines[index] ?? "";
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fence = line.match(/^\s*```/);
    if (fence) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !/^\s*```/.test(lines[index] ?? "")) {
        code.push(lines[index] ?? "");
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push({ kind: "code", text: code.join("\n") });
      continue;
    }

    const heading = line.match(/^(#{1,5})\s+(.+?)\s*#*\s*$/);
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1]!.length as 1 | 2 | 3 | 4 | 5, content: previewText(heading[2]!) });
      index += 1;
      continue;
    }

    const parsedTable = parseTableAt(lines, index);
    if (parsedTable) {
      blocks.push({ kind: "table", table: parsedTable.table });
      index = parsedTable.nextIndex;
      continue;
    }

    if (taskLinePattern.test(line)) {
      const items: PreviewTask[] = [];
      while (index < lines.length) {
        const task = (lines[index] ?? "").match(taskLinePattern);
        if (!task) break;
        items.push({ checked: task[1]!.toLowerCase() === "x", ...previewText(task[2] ?? ""), lineIndex: index });
        index += 1;
      }
      blocks.push({ kind: "tasks", items });
      continue;
    }

    if (unorderedLinePattern.test(line)) {
      const items: PreviewText[] = [];
      while (index < lines.length) {
        const bullet = (lines[index] ?? "").match(unorderedLinePattern);
        if (!bullet || taskLinePattern.test(lines[index] ?? "")) break;
        items.push(previewText(bullet[1] ?? ""));
        index += 1;
      }
      blocks.push({ kind: "unordered", items });
      continue;
    }

    if (orderedLinePattern.test(line)) {
      const items: PreviewText[] = [];
      while (index < lines.length) {
        const ordered = (lines[index] ?? "").match(orderedLinePattern);
        if (!ordered) break;
        items.push(previewText(ordered[1] ?? ""));
        index += 1;
      }
      blocks.push({ kind: "ordered", items });
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length) {
        const quoted = (lines[index] ?? "").match(/^\s*>\s?(.*)$/);
        if (!quoted) break;
        quote.push(quoted[1]!);
        index += 1;
      }
      blocks.push({ kind: "quote", content: previewText(quote.join(" ")) });
      continue;
    }

    const paragraph = [line.trim()];
    index += 1;
    while (index < lines.length && (lines[index] ?? "").trim() && !isBlockStart(lines, index)) {
      paragraph.push((lines[index] ?? "").trim());
      index += 1;
    }
    blocks.push({ kind: "paragraph", content: previewText(paragraph.join(" ")) });
  }

  return blocks;
}

function firstHeadingMatches(markdown: string, hiddenHeading: string | readonly string[] | undefined): boolean {
  if (hiddenHeading === undefined) return false;
  const firstBlock = parseMarkdownBlocks(markdown)[0];
  if (firstBlock?.kind !== "heading" || firstBlock.level !== 1) return false;
  const headingText = firstBlock.content.text.trim();
  const hiddenHeadings = typeof hiddenHeading === "string" ? [hiddenHeading] : hiddenHeading;
  return hiddenHeadings.some((candidate) => headingText === candidate.trim());
}

const inlinePatternSource = String.raw`(\*\*\*[^*\n]+\*\*\*|___[^_\n]+___|\*\*[^*\n]+\*\*|__[^_\n]+__|==[^=\n]+==|\+\+[^+\n]+\+\+|~~[^~\n]+~~|\`[^\`\n]+\`|\[[^\]\n]+\]\([^)\n]+\)|\*[^*\n]+\*|_[^_\n]+_)`;

function safeHref(href: string) {
  return /^(https?:|mailto:)/i.test(href) ? href : undefined;
}

const namedHtmlEntities: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: "\u00a0",
  quot: '"'
};

function decodeHtmlEntities(source: string): string {
  return source.replace(/&(?:#(\d+)|#x([\da-f]+)|([a-z][\da-z]+));/gi, (entity, decimal: string | undefined, hexadecimal: string | undefined, named: string | undefined) => {
    if (named) return namedHtmlEntities[named.toLowerCase()] ?? entity;
    const codePoint = Number.parseInt(decimal ?? hexadecimal ?? "", decimal ? 10 : 16);
    return Number.isFinite(codePoint) && codePoint <= 0x10ffff && !(codePoint >= 0xd800 && codePoint <= 0xdfff)
      ? String.fromCodePoint(codePoint)
      : entity;
  });
}

function decodePreviewText(source: string): string {
  return decodeHtmlEntities(source).replace(/\\([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])/g, "$1");
}

function renderInline(source: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const inlinePattern = new RegExp(inlinePatternSource, "g");
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = inlinePattern.exec(source))) {
    if (match.index > cursor) nodes.push(decodePreviewText(source.slice(cursor, match.index)));
    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;

    if (token.startsWith("***") || token.startsWith("___")) nodes.push(<strong key={key}><em>{renderInline(token.slice(3, -3), key)}</em></strong>);
    else if (token.startsWith("**") || token.startsWith("__")) nodes.push(<strong key={key}>{renderInline(token.slice(2, -2), key)}</strong>);
    else if (token.startsWith("==")) nodes.push(<mark key={key}>{renderInline(token.slice(2, -2), key)}</mark>);
    else if (token.startsWith("++")) nodes.push(<u key={key}>{renderInline(token.slice(2, -2), key)}</u>);
    else if (token.startsWith("~~")) nodes.push(<s key={key}>{renderInline(token.slice(2, -2), key)}</s>);
    else if (token.startsWith("`")) nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    else if (token.startsWith("[")) {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const href = link ? safeHref(link[2]!) : undefined;
      nodes.push(href
        ? <span key={key} className="bedrock-preview-link">{renderInline(link![1]!, key)}</span>
        : decodePreviewText(token));
    } else nodes.push(<em key={key}>{renderInline(token.slice(1, -1), key)}</em>);

    cursor = match.index + token.length;
  }

  if (cursor < source.length) nodes.push(decodePreviewText(source.slice(cursor)));
  return nodes;
}

function toggleTaskAtLine(markdown: string, lineIndex: number, checked: boolean) {
  const parts = markdown.split(/(\r\n|\r|\n)/);
  let currentLine = -1;
  return parts.map((part, index) => {
    if (index % 2 !== 0 || ++currentLine !== lineIndex) return part;
    return part.replace(/^(\s*[-*+]\s+\[)[ xX](\])/, `$1${checked ? "x" : " "}$2`);
  }).join("");
}

export function MarkdownPreview({ markdown, className = "", empty = "Write something…", hiddenHeading, onTaskToggle }: MarkdownPreviewProps) {
  const parsed = parseMarkdownBlocks(markdown);
  const hiddenHeadings = hiddenHeading === undefined ? [] : typeof hiddenHeading === "string" ? [hiddenHeading] : hiddenHeading;
  const hiddenHeadingIndex = hiddenHeading === undefined ? -1 : parsed.findIndex((block) =>
    block.kind === "heading" && hiddenHeadings.some((heading) => block.content.text.trim() === heading.trim())
  );
  const blocks = hiddenHeadingIndex >= 0 ? parsed.filter((_, index) => index !== hiddenHeadingIndex) : parsed;
  const classes = `bedrock-markdown-preview ${className}`.trim();

  if (!blocks.length) return <div className={`${classes} is-empty`}>{empty}</div>;
  return <div className={classes}>
    {blocks.map((block, blockIndex) => {
      const key = `block-${blockIndex}`;
      if (block.kind === "heading") {
        const content = renderInline(block.content.text, key);
        const style = { textAlign: block.content.textAlign };
        if (block.level === 1) return <h1 key={key} style={style}>{content}</h1>;
        if (block.level === 2) return <h2 key={key} style={style}>{content}</h2>;
        if (block.level === 3) return <h3 key={key} style={style}>{content}</h3>;
        if (block.level === 4) return <h4 key={key} style={style}>{content}</h4>;
        return <h5 key={key} style={style}>{content}</h5>;
      }
      if (block.kind === "paragraph") return <p key={key} style={{ textAlign: block.content.textAlign }}>{renderInline(block.content.text, key)}</p>;
      if (block.kind === "quote") return <blockquote key={key} style={{ textAlign: block.content.textAlign }}>{renderInline(block.content.text, key)}</blockquote>;
      if (block.kind === "code") return <pre key={key}><code>{block.text}</code></pre>;
      if (block.kind === "table") return <div key={key} className="bedrock-table-scroll">
        <table>
          <thead><tr>{block.table.headers.map((cell, cellIndex) => <th key={`${key}-header-${cellIndex}`} scope="col" style={{ textAlign: cell.textAlign ?? block.table.alignments[cellIndex] ?? undefined }}>{renderInline(cell.text, `${key}-header-${cellIndex}`)}</th>)}</tr></thead>
          <tbody>{block.table.rows.map((row, rowIndex) => <tr key={`${key}-row-${rowIndex}`}>
            {row.map((cell, cellIndex) => <td key={`${key}-row-${rowIndex}-${cellIndex}`} style={{ textAlign: cell.textAlign ?? block.table.alignments[cellIndex] ?? undefined }}>{renderInline(cell.text, `${key}-row-${rowIndex}-${cellIndex}`)}</td>)}
          </tr>)}</tbody>
        </table>
      </div>;
      if (block.kind === "tasks") return <ul key={key} className="bedrock-preview-tasks">
        {block.items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`} className={item.checked ? "is-checked" : ""} style={{ textAlign: item.textAlign }}>
          {onTaskToggle
            ? <button
              type="button"
              role="checkbox"
              aria-checked={item.checked}
              aria-label={item.text || "Untitled task"}
              title={item.checked ? "Mark incomplete" : "Mark complete"}
              className="bedrock-preview-checkbox-button"
              onClick={() => onTaskToggle(toggleTaskAtLine(markdown, item.lineIndex, !item.checked))}
            ><span className="bedrock-preview-checkbox" aria-hidden="true"/></button>
            : <span className="bedrock-preview-checkbox" aria-hidden="true"/>}
          <span>{renderInline(item.text, `${key}-${itemIndex}`)}</span>
        </li>)}
      </ul>;
      const List = block.kind === "ordered" ? "ol" : "ul";
      return <List key={key}>{block.items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`} style={{ textAlign: item.textAlign }}>{renderInline(item.text, `${key}-${itemIndex}`)}</li>)}</List>;
    })}
  </div>;
}

const emptyParagraphMarkdown = "&nbsp;";

function alignmentFromMarkdownTokens(tokens: MarkdownToken[]): { tokens: MarkdownToken[]; textAlign?: TextAlignment } {
  const result = tokens.map((token) => ({ ...token }));
  const last = result.at(-1);
  const marker = last?.type === "html" ? (last.raw ?? last.text ?? "").trim().match(textAlignmentMarkerPattern) : null;
  if (!marker) return { tokens: result };

  result.pop();
  const previous = result.at(-1);
  if (previous?.type === "text") {
    if (typeof previous.raw === "string") previous.raw = previous.raw.trimEnd();
    if (typeof previous.text === "string") previous.text = previous.text.trimEnd();
    if (!previous.raw && !previous.text) result.pop();
  }
  return { tokens: result, textAlign: marker[1]!.toLowerCase() as TextAlignment };
}

function appendAlignmentMarker(markdown: string, textAlign: unknown): string {
  if (typeof textAlign !== "string" || textAlign === "left") return markdown;
  const content = markdown || emptyParagraphMarkdown;
  return `${content} <!-- bedrock-text-align:${textAlign} -->`;
}

const AlignedParagraph = Paragraph.extend({
  parseMarkdown: (token: MarkdownToken, helpers: MarkdownParseHelpers) => {
    const aligned = alignmentFromMarkdownTokens(token.tokens ?? []);
    if (aligned.tokens.length === 1 && aligned.tokens[0]?.type === "image") {
      return helpers.parseChildren(aligned.tokens);
    }

    const content = helpers.parseInline(aligned.tokens);
    const onlyEmptyMarker = aligned.tokens.length === 1
      && aligned.tokens[0]?.type === "text"
      && [emptyParagraphMarkdown, "\u00a0"].includes(aligned.tokens[0].raw ?? aligned.tokens[0].text ?? "");
    return helpers.createNode("paragraph", { textAlign: aligned.textAlign ?? "left" }, onlyEmptyMarker ? [] : content);
  },
  renderMarkdown: (node: JSONContent, helpers: MarkdownRendererHelpers, context: RenderContext) => {
    const content = Array.isArray(node.content) ? node.content : [];
    let markdown = "";
    if (content.length > 0) markdown = helpers.renderChildren(content);
    else {
      const previousContent = Array.isArray(context.previousNode?.content) ? context.previousNode.content : [];
      if (context.previousNode?.type === "paragraph" && previousContent.length === 0) markdown = emptyParagraphMarkdown;
    }
    return appendAlignmentMarker(markdown, node.attrs?.["textAlign"]);
  }
});

const AlignedHeading = Heading.extend({
  parseMarkdown: (token: MarkdownToken, helpers: MarkdownParseHelpers) => {
    const aligned = alignmentFromMarkdownTokens(token.tokens ?? []);
    return helpers.createNode("heading", { level: token.depth ?? 1, textAlign: aligned.textAlign ?? "left" }, helpers.parseInline(aligned.tokens));
  },
  renderMarkdown: (node: JSONContent, helpers: MarkdownRendererHelpers) => {
    const level = typeof node.attrs?.["level"] === "number" ? node.attrs["level"] : 1;
    const markdown = `${"#".repeat(level)} ${helpers.renderChildren(node.content ?? [])}`;
    return appendAlignmentMarker(markdown, node.attrs?.["textAlign"]);
  }
});

function runTextFormattingAction(editor: Editor, action: MarkdownEditorShortcutAction): boolean {
  switch (action) {
    case "text-bold": return editor.chain().focus().toggleBold().run();
    case "text-italic": return editor.chain().focus().toggleItalic().run();
    case "text-underline": return editor.chain().focus().toggleUnderline().run();
    case "text-paragraph": return editor.chain().focus().setParagraph().run();
    case "text-heading-one": return editor.chain().focus().toggleHeading({ level: 1 }).run();
    case "text-heading-two": return editor.chain().focus().toggleHeading({ level: 2 }).run();
    case "text-heading-three": return editor.chain().focus().toggleHeading({ level: 3 }).run();
    case "text-heading-four": return editor.chain().focus().toggleHeading({ level: 4 }).run();
    case "text-heading-five": return editor.chain().focus().toggleHeading({ level: 5 }).run();
    case "text-align-left": return editor.chain().focus().setTextAlign("left").run();
    case "text-align-center": return editor.chain().focus().setTextAlign("center").run();
    case "text-align-right": return editor.chain().focus().setTextAlign("right").run();
    case "text-align-justify": return editor.chain().focus().setTextAlign("justify").run();
    case "text-bullet-list": return editor.chain().focus().toggleBulletList().run();
    case "text-ordered-list": return editor.chain().focus().toggleOrderedList().run();
    case "text-checklist": return editor.chain().focus().toggleTaskList().run();
    case "text-quote": return editor.chain().focus().toggleBlockquote().run();
    case "text-code-block": return editor.chain().focus().toggleCodeBlock().run();
    case "text-highlight": return editor.chain().focus().toggleHighlight().run();
  }
}

function nativeTextFormattingActionForEvent(event: KeyboardEvent): MarkdownEditorShortcutAction | null {
  const modified = event.metaKey !== event.ctrlKey && (event.metaKey || event.ctrlKey);
  if (!modified) return null;
  const code = event.code;
  if (!event.altKey && !event.shiftKey && code === "KeyB") return "text-bold";
  if (!event.altKey && !event.shiftKey && code === "KeyI") return "text-italic";
  if (!event.altKey && !event.shiftKey && code === "KeyU") return "text-underline";
  if (event.altKey && !event.shiftKey && /^Digit[1-5]$/.test(code)) {
    return ({
      Digit1: "text-heading-one",
      Digit2: "text-heading-two",
      Digit3: "text-heading-three",
      Digit4: "text-heading-four",
      Digit5: "text-heading-five"
    } as const)[code as "Digit1" | "Digit2" | "Digit3" | "Digit4" | "Digit5"];
  }
  if (!event.altKey && event.shiftKey && code === "Digit8") return "text-bullet-list";
  if (!event.altKey && event.shiftKey && code === "Digit7") return "text-ordered-list";
  if (!event.altKey && event.shiftKey && code === "Digit9") return "text-checklist";
  if (!event.altKey && event.shiftKey && code === "KeyB") return "text-quote";
  if (event.altKey && !event.shiftKey && code === "Digit0") return "text-code-block";
  if (!event.altKey && event.shiftKey && code === "KeyL") return "text-align-left";
  if (!event.altKey && event.shiftKey && code === "KeyE") return "text-align-center";
  if (!event.altKey && event.shiftKey && code === "KeyR") return "text-align-right";
  if (!event.altKey && event.shiftKey && code === "KeyJ") return "text-align-justify";
  return null;
}

const linkShortcut = typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform)
  ? { display: "⌘K", aria: "Meta+K" }
  : { display: "Ctrl+K", aria: "Control+K" };

function isLinkShortcut(event: KeyboardEvent) {
  return event.metaKey !== event.ctrlKey && !event.altKey && !event.shiftKey && event.code === "KeyK";
}

function Tool({ label, icon, shortcut, active, onClick }: { label: string; icon: IconName; shortcut?: { display: string; aria: string } | undefined; active?: boolean; onClick: () => void }) {
  return <Tooltip label={label} shortcut={shortcut?.display}><button
    type="button"
    aria-label={shortcut ? `${label} (${shortcut.display})` : label}
    aria-keyshortcuts={shortcut?.aria}
    className={active ? "is-active" : ""}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
  ><Icon name={icon}/></button></Tooltip>;
}

function EditorToolbar({ editor, shortcuts, variant = "default", enableTables = false, onEditLink }: { editor: Editor; shortcuts?: MarkdownEditorShortcuts | undefined; variant?: "default" | "floating" | "selection"; enableTables?: boolean; onEditLink: () => void }) {
  const active = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      bold: instance.isActive("bold"),
      italic: instance.isActive("italic"),
      underline: instance.isActive("underline"),
      paragraph: instance.isActive("paragraph"),
      headingOne: instance.isActive("heading", { level: 1 }),
      headingTwo: instance.isActive("heading", { level: 2 }),
      headingThree: instance.isActive("heading", { level: 3 }),
      headingFour: instance.isActive("heading", { level: 4 }),
      headingFive: instance.isActive("heading", { level: 5 }),
      alignLeft: instance.isActive({ textAlign: "left" }),
      alignCenter: instance.isActive({ textAlign: "center" }),
      alignRight: instance.isActive({ textAlign: "right" }),
      justify: instance.isActive({ textAlign: "justify" }),
      bulletList: instance.isActive("bulletList"),
      orderedList: instance.isActive("orderedList"),
      checklist: instance.isActive("taskList"),
      quote: instance.isActive("blockquote"),
      codeBlock: instance.isActive("codeBlock"),
      highlight: instance.isActive("highlight"),
      link: instance.isActive("link")
    })
  });
  const variantClass = variant === "default" ? "" : `bedrock-editor-toolbar--${variant}`;
  return <div className={`bedrock-editor-toolbar ${variantClass}`.trim()} role="toolbar" aria-label={variant === "selection" ? "Format selected text" : "Text formatting"}>
    <Tool label="Bold" icon="bold" shortcut={shortcuts?.actions["text-bold"]} active={active.bold} onClick={() => runTextFormattingAction(editor, "text-bold")}/>
    <Tool label="Italic" icon="italic" shortcut={shortcuts?.actions["text-italic"]} active={active.italic} onClick={() => runTextFormattingAction(editor, "text-italic")}/>
    <Tool label="Underline" icon="underline" shortcut={shortcuts?.actions["text-underline"]} active={active.underline} onClick={() => runTextFormattingAction(editor, "text-underline")}/>
    <Tool label="Paragraph" icon="paragraph" shortcut={shortcuts?.actions["text-paragraph"]} active={active.paragraph} onClick={() => runTextFormattingAction(editor, "text-paragraph")}/>
    <Tool label="Heading 1" icon="heading-one" shortcut={shortcuts?.actions["text-heading-one"]} active={active.headingOne} onClick={() => runTextFormattingAction(editor, "text-heading-one")}/>
    <Tool label="Heading 2" icon="heading-two" shortcut={shortcuts?.actions["text-heading-two"]} active={active.headingTwo} onClick={() => runTextFormattingAction(editor, "text-heading-two")}/>
    <Tool label="Heading 3" icon="heading-three" shortcut={shortcuts?.actions["text-heading-three"]} active={active.headingThree} onClick={() => runTextFormattingAction(editor, "text-heading-three")}/>
    <Tool label="Heading 4" icon="heading-four" shortcut={shortcuts?.actions["text-heading-four"]} active={active.headingFour} onClick={() => runTextFormattingAction(editor, "text-heading-four")}/>
    <Tool label="Heading 5" icon="heading-five" shortcut={shortcuts?.actions["text-heading-five"]} active={active.headingFive} onClick={() => runTextFormattingAction(editor, "text-heading-five")}/>
    <span />
    <Tool label="Align left" icon="align-left" shortcut={shortcuts?.actions["text-align-left"]} active={active.alignLeft} onClick={() => runTextFormattingAction(editor, "text-align-left")}/>
    <Tool label="Align center" icon="align-center" shortcut={shortcuts?.actions["text-align-center"]} active={active.alignCenter} onClick={() => runTextFormattingAction(editor, "text-align-center")}/>
    <Tool label="Align right" icon="align-right" shortcut={shortcuts?.actions["text-align-right"]} active={active.alignRight} onClick={() => runTextFormattingAction(editor, "text-align-right")}/>
    <Tool label="Justify" icon="align-justify" shortcut={shortcuts?.actions["text-align-justify"]} active={active.justify} onClick={() => runTextFormattingAction(editor, "text-align-justify")}/>
    <span />
    <Tool label="Bulleted list" icon="list" shortcut={shortcuts?.actions["text-bullet-list"]} active={active.bulletList} onClick={() => runTextFormattingAction(editor, "text-bullet-list")}/>
    <Tool label="Numbered list" icon="list-ordered" shortcut={shortcuts?.actions["text-ordered-list"]} active={active.orderedList} onClick={() => runTextFormattingAction(editor, "text-ordered-list")}/>
    <Tool label="Checklist" icon="list-checks" shortcut={shortcuts?.actions["text-checklist"]} active={active.checklist} onClick={() => runTextFormattingAction(editor, "text-checklist")}/>
    <Tool label="Quote" icon="quote" shortcut={shortcuts?.actions["text-quote"]} active={active.quote} onClick={() => runTextFormattingAction(editor, "text-quote")}/>
    <Tool label="Code block" icon="code" shortcut={shortcuts?.actions["text-code-block"]} active={active.codeBlock} onClick={() => runTextFormattingAction(editor, "text-code-block")}/>
    <Tool label="Link" icon="link" shortcut={linkShortcut} active={active.link} onClick={onEditLink}/>
    <Tool label="Highlight" icon="highlighter" shortcut={shortcuts?.actions["text-highlight"]} active={active.highlight} onClick={() => runTextFormattingAction(editor, "text-highlight")}/>
    {enableTables && <><span/><Tool label="Insert table" icon="grid" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}/></>}
  </div>;
}

function TableToolbar({ editor }: { editor: Editor }) {
  const tableActive = useEditorState({ editor, selector: ({ editor: instance }) => instance.isActive("table") });
  if (!tableActive) return null;
  const run = (command: "addRowAfter" | "addColumnAfter" | "deleteRow" | "deleteColumn" | "deleteTable") => editor.chain().focus()[command]().run();
  return <div className="bedrock-table-toolbar" role="toolbar" aria-label="Table editing">
    <span>Table</span>
    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => run("addRowAfter")}>Add row</button>
    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => run("addColumnAfter")}>Add column</button>
    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => run("deleteRow")}>Remove row</button>
    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => run("deleteColumn")}>Remove column</button>
    <button type="button" className="is-destructive" onMouseDown={(event) => event.preventDefault()} onClick={() => run("deleteTable")}>Delete table</button>
  </div>;
}

const selectionToolbarOptions: NonNullable<BubbleMenuProps["options"]> = {
  strategy: "fixed",
  placement: "top",
  offset: 10,
  flip: true,
  shift: { padding: 12 },
  inline: true
};

function selectionToolbarContainer() {
  return document.body;
}

function SelectionToolbar({ editor, shortcuts, onEditLink }: { editor: Editor; shortcuts?: MarkdownEditorShortcuts | undefined; onEditLink: () => void }) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const startMouseSelection = (event: MouseEvent) => {
      if (event.button === 0) menuRef.current?.classList.add("is-selecting-with-mouse");
    };
    const finishMouseSelection = () => menuRef.current?.classList.remove("is-selecting-with-mouse");

    editor.view.dom.addEventListener("mousedown", startMouseSelection);
    window.addEventListener("mouseup", finishMouseSelection);
    window.addEventListener("blur", finishMouseSelection);
    return () => {
      editor.view.dom.removeEventListener("mousedown", startMouseSelection);
      window.removeEventListener("mouseup", finishMouseSelection);
      window.removeEventListener("blur", finishMouseSelection);
    };
  }, [editor]);

  return <BubbleMenu
    ref={menuRef}
    editor={editor}
    updateDelay={0}
    appendTo={selectionToolbarContainer}
    options={selectionToolbarOptions}
    className="bedrock-selection-menu"
  >
    <EditorToolbar editor={editor} shortcuts={shortcuts} variant="selection" onEditLink={onEditLink}/>
  </BubbleMenu>;
}

export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(function MarkdownEditor({ markdown, onChange, onCommit, hiddenFirstHeading, autofocus = false, compact = false, focusPoint, showToolbar, floatingToolbar = false, bottomScrollClearance, enableTables = false, shortcuts, initialCursor, onCursorChange }, ref) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchInput = useRef<HTMLInputElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [replacementText, setReplacementText] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [activeMatchIndex, setActiveMatchIndex] = useState(-1);
  const [matchCount, setMatchCount] = useState(0);
  const [linkEditRequest, setLinkEditRequest] = useState<LinkEditRequest | null>(null);
  const scrollClearance = bottomScrollClearance ?? (floatingToolbar ? EDITOR_TOOLBAR_SCROLL_CLEARANCE : 0);
  const restoredCursor = useRef(false);
  const latestCommit = useRef(onCommit);
  const lastCommitted = useRef(markdown);
  latestCommit.current = onCommit;
  const commit = (value: string) => {
    if (value === lastCommitted.current) return;
    lastCommitted.current = value;
    latestCommit.current?.(value);
  };
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, paragraph: false, link: { openOnClick: false, defaultProtocol: "https" } }),
      AlignedHeading,
      AlignedParagraph,
      TextAlign.configure({ types: ["heading", "paragraph"], defaultAlignment: "left" }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight,
      TableKit.configure({ table: { renderWrapper: true } }),
      DocumentSearch,
      Markdown.configure({ markedOptions: { gfm: true } })
    ],
    content: markdown,
    contentType: "markdown",
    autofocus,
    immediatelyRender: false,
    onUpdate: ({ editor: instance }) => {
      const next = instance.getMarkdown();
      onChange(next);
      const search = documentSearchPluginKey.getState(instance.state);
      if (search) {
        setActiveMatchIndex(search.activeIndex);
        setMatchCount(search.matches.length);
      }
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => commit(next), 2000);
    },
    onBlur: ({ editor: instance }) => commit(instance.getMarkdown()),
    onSelectionUpdate: ({ editor: instance }) => {
      if (initialCursor !== undefined && !restoredCursor.current) return;
      onCursorChange?.(instance.state.selection.from);
    },
    editorProps: {
      attributes: {
        "aria-label": "Markdown content",
        autocorrect: "off",
        class: "bedrock-prose",
        spellcheck: "false"
      },
      ...(scrollClearance > 0 ? {
        scrollThreshold: { top: 0, right: 0, bottom: scrollClearance, left: 0 },
        scrollMargin: { top: 5, right: 5, bottom: scrollClearance, left: 5 }
      } : {})
    }
  });

  useEffect(() => {
    if (!editor || !focusPoint) return;
    const frame = requestAnimationFrame(() => {
      const position = editor.view.posAtCoords({ left: focusPoint.x, top: focusPoint.y });
      editor.commands.focus(position?.pos ?? "end");
    });
    return () => cancelAnimationFrame(frame);
  }, [editor, focusPoint]);

  useEffect(() => {
    if (!editor || restoredCursor.current || initialCursor === undefined) return;
    editor.commands.focus(Math.max(1, Math.min(initialCursor, editor.state.doc.content.size)), { scrollIntoView: false });
    restoredCursor.current = true;
  }, [editor, initialCursor]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (editor && !editor.isDestroyed) commit(editor.getMarkdown());
  }, [editor]);

  const applySearch = (query: string, requestedIndex: number, matchCase = caseSensitive) => {
    if (!editor) return;
    const search = setDocumentSearch(editor, query, requestedIndex, matchCase);
    setActiveMatchIndex(search.activeIndex);
    setMatchCount(search.matches.length);
  };

  const openSearch = () => {
    setSearchOpen(true);
    applySearch(searchQuery, activeMatchIndex < 0 ? 0 : activeMatchIndex);
    requestAnimationFrame(() => {
      searchInput.current?.focus();
      searchInput.current?.select();
    });
  };

  const closeSearch = () => {
    setSearchOpen(false);
    applySearch("", -1);
    editor?.commands.focus();
  };

  const navigateSearch = (direction: 1 | -1) => {
    if (!searchQuery.trim()) return;
    applySearch(searchQuery, activeMatchIndex < 0 ? 0 : activeMatchIndex + direction);
  };

  const toggleCaseSensitive = () => {
    const nextCaseSensitive = !caseSensitive;
    setCaseSensitive(nextCaseSensitive);
    applySearch(searchQuery, 0, nextCaseSensitive);
  };

  const replaceCurrentMatch = () => {
    if (!editor) return;
    const search = documentSearchPluginKey.getState(editor.state);
    const match = search?.matches[search.activeIndex];
    if (!match) return;

    editor.view.dispatch(editor.state.tr.insertText(replacementText, match.from, match.to));
    const updatedSearch = documentSearchPluginKey.getState(editor.state);
    const nextMatchIndex = updatedSearch?.matches.findIndex((candidate) => candidate.from >= match.from + replacementText.length) ?? -1;
    applySearch(searchQuery, nextMatchIndex >= 0 ? nextMatchIndex : 0);
  };

  const replaceAllMatches = () => {
    if (!editor) return;
    const matches = documentSearchPluginKey.getState(editor.state)?.matches ?? [];
    if (matches.length === 0) return;

    let transaction = editor.state.tr;
    for (const match of [...matches].reverse()) {
      transaction = transaction.insertText(replacementText, match.from, match.to);
    }
    editor.view.dispatch(transaction);
    applySearch(searchQuery, 0);
  };

  useImperativeHandle(ref, () => ({
    focusFirstParagraph: () => {
      if (!editor) return false;
      let paragraphPosition: number | undefined;
      editor.state.doc.forEach((node, offset) => {
        if (paragraphPosition === undefined && node.type.name === "paragraph") paragraphPosition = offset + 1;
      });
      if (paragraphPosition !== undefined) return editor.commands.focus(paragraphPosition);

      const insertionPosition = editor.state.doc.content.size;
      return editor.chain()
        .insertContentAt(insertionPosition, { type: "paragraph" })
        .focus(insertionPosition + 1)
        .run();
    },
    openSearch
  }), [editor, searchQuery, activeMatchIndex, caseSensitive]);

  if (!editor) return null;
  const firstHeadingHidden = firstHeadingMatches(markdown, hiddenFirstHeading);
  const toolbarVisible = showToolbar ?? !compact;
  const editLink = () => setLinkEditRequest(linkEditRequestFromSelection(editor));
  const toolbar = toolbarVisible ? <EditorToolbar editor={editor} shortcuts={shortcuts} variant={floatingToolbar ? "floating" : "default"} enableTables={enableTables} onEditLink={editLink}/> : null;
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (isDocumentSearchShortcut(event.nativeEvent)) {
      event.preventDefault();
      event.stopPropagation();
      openSearch();
      return;
    }
    if (searchOpen && !event.nativeEvent.isComposing && event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeSearch();
      return;
    }
    if (searchOpen && !event.nativeEvent.isComposing && event.key === "Enter" && event.target === searchInput.current) {
      event.preventDefault();
      event.stopPropagation();
      navigateSearch(event.shiftKey ? -1 : 1);
      return;
    }
    if (searchOpen && !event.nativeEvent.isComposing && event.key === "Enter" && event.target === replaceInput.current) {
      event.preventDefault();
      event.stopPropagation();
      replaceCurrentMatch();
      return;
    }
    if (event.target instanceof Element && event.target.closest(".bedrock-document-search, .bedrock-link-menu")) return;
    if (isLinkShortcut(event.nativeEvent)) {
      event.preventDefault();
      event.stopPropagation();
      editLink();
      return;
    }
    if (!shortcuts) return;
    const action = shortcuts.actionForEvent(event.nativeEvent);
    const defaultAction = shortcuts.defaultActionForEvent(event.nativeEvent);
    const nativeAction = nativeTextFormattingActionForEvent(event.nativeEvent);
    if (!action && !defaultAction && !nativeAction) return;
    event.preventDefault();
    event.stopPropagation();
    if (action) runTextFormattingAction(editor, action);
  };
  return <div className={`bedrock-editor ${compact ? "bedrock-editor--compact" : ""} ${firstHeadingHidden ? "bedrock-editor--hidden-first-heading" : ""}`.trim()} onKeyDownCapture={handleKeyDown}>
    {toolbar && floatingToolbar && typeof document !== "undefined" ? createPortal(toolbar, document.body) : toolbar}
    {searchOpen && <div className="bedrock-document-search" role="search" aria-label="Find in document">
      <div className="bedrock-document-search__row">
        <Icon name="search"/>
        <input
          ref={searchInput}
          value={searchQuery}
          onChange={(event) => {
            const query = event.target.value;
            setSearchQuery(query);
            applySearch(query, 0);
          }}
          placeholder="Find in document"
          aria-label="Find in document"
        />
        <span className="bedrock-document-search__status" role="status" aria-live="polite">
          {searchQuery.trim() ? (matchCount > 0 ? `${activeMatchIndex + 1} of ${matchCount}` : "No results") : "Type to find"}
        </span>
        <button
          className="bedrock-document-search__case-button"
          type="button"
          aria-label="Match case"
          aria-pressed={caseSensitive}
          title="Match case"
          onMouseDown={(event) => event.preventDefault()}
          onClick={toggleCaseSensitive}
        >Aa</button>
        <button type="button" disabled={matchCount === 0} aria-label="Previous match" onMouseDown={(event) => event.preventDefault()} onClick={() => navigateSearch(-1)}><Icon name="arrow-left"/></button>
        <button type="button" disabled={matchCount === 0} aria-label="Next match" onMouseDown={(event) => event.preventDefault()} onClick={() => navigateSearch(1)}><Icon name="arrow-right"/></button>
        <button type="button" aria-label="Close find" onMouseDown={(event) => event.preventDefault()} onClick={closeSearch}><Icon name="close"/></button>
      </div>
      <div className="bedrock-document-search__row bedrock-document-search__row--replace">
        <Icon name="arrow-right"/>
        <input
          ref={replaceInput}
          value={replacementText}
          onChange={(event) => setReplacementText(event.target.value)}
          placeholder="Replace with"
          aria-label="Replace with"
        />
        <button className="bedrock-document-search__text-button" type="button" disabled={matchCount === 0} aria-label="Replace current match" onMouseDown={(event) => event.preventDefault()} onClick={replaceCurrentMatch}>Replace</button>
        <button className="bedrock-document-search__text-button" type="button" disabled={matchCount === 0} aria-label="Replace all matches" onMouseDown={(event) => event.preventDefault()} onClick={replaceAllMatches}>Replace all</button>
      </div>
    </div>}
    {enableTables && toolbarVisible && !floatingToolbar && <TableToolbar editor={editor}/>}
    {toolbarVisible && typeof document !== "undefined" && <SelectionToolbar editor={editor} shortcuts={shortcuts} onEditLink={editLink}/>}
    <LinkMenu editor={editor} editRequest={linkEditRequest} onEditRequest={setLinkEditRequest}/>
    <EditorContent editor={editor}/>
  </div>;
});
